/**
 * Instant navigation cache (stale-while-revalidate) for GET /api requests.
 *
 * Why: every page used to wait for its network fetch before rendering, so each
 * navigation felt like a ~1s blank/spinner. With this cache a page that was
 * already visited (or prefetched on sidebar hover) resolves from memory in the
 * same tick, then silently refreshes in the background.
 *
 * Any mutating request (POST/PUT/PATCH/DELETE) drops the cache so the user never
 * sees stale data after making a change.
 */

const TTL_MS = 45_000;
// Endpoint yang harus selalu segar (polling status, progres, verifikasi, unduhan)
const NEVER_CACHE = [
  '/status', '/check/', '/connection', '/qr', '/progress', '/verify',
  '/unread-count', '/export', '/pdf', '/health', '/poll', '/sse', '/stream',
  // Inbox harus real-time: percakapan & pesan tidak boleh dilayani dari cache
  '/conversations', '/messages',
];
const cache = new Map(); // url -> { data, status, headers, at }
const inflight = new Map(); // url -> Promise

const isApiGet = (config) =>
  (config.method || 'get').toLowerCase() === 'get' &&
  String(config.url || '').includes('/api/') &&
  !NEVER_CACHE.some((p) => String(config.url || '').includes(p));

const keyOf = (config) => {
  const url = config.url || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return url + params;
};

export const invalidateApiCache = (match) => {
  if (!match) return cache.clear();
  for (const k of [...cache.keys()]) if (k.includes(match)) cache.delete(k);
};

export const installApiCache = (axios) => {
  if (axios.__neravaCacheInstalled) return;
  axios.__neravaCacheInstalled = true;

  axios.interceptors.request.use((config) => {
    if (config.__noCache) return config;

    if (!isApiGet(config)) {
      // A mutation happened — everything cached may now be stale.
      if (['post', 'put', 'patch', 'delete'].includes((config.method || '').toLowerCase())) {
        invalidateApiCache();
      }
      return config;
    }

    const key = keyOf(config);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) {
      // Serve from memory instantly, refresh in the background.
      config.adapter = () =>
        Promise.resolve({ data: hit.data, status: hit.status, statusText: 'OK', headers: hit.headers, config });
      revalidate(axios, config, key);
    }
    return config;
  });

  axios.interceptors.response.use((response) => {
    if (isApiGet(response.config) && response.status === 200) {
      cache.set(keyOf(response.config), {
        data: response.data,
        status: response.status,
        headers: response.headers,
        at: Date.now(),
      });
    }
    return response;
  });
};

const revalidate = (axios, config, key) => {
  if (inflight.has(key)) return;
  const p = axios
    .request({
      url: config.url,
      method: 'get',
      params: config.params,
      headers: config.headers,
      __noCache: true,
    })
    .catch(() => {})
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
};

/** Warm the cache before the user clicks (sidebar hover). */
export const prefetchApi = (axios, url, headers) => {
  const key = url;
  const hit = cache.get(key);
  if ((hit && Date.now() - hit.at < TTL_MS) || inflight.has(key)) return;
  const p = axios
    .get(url, { headers, __noCache: true })
    .catch(() => {})
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
};
